import React, { useState, useEffect, useRef } from 'react';
import { Eye, Target, Heart, Users, Award, Shield, MapPin, Phone, Mail, Globe, CheckCircle, ArrowRight, Star, TrendingUp } from 'lucide-react';

const AboutPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [imageLoadErrors, setImageLoadErrors] = useState(new Set());
  const sectionRefs = useRef({});

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.section]));
          }
        });
      },
      { threshold: 0.1 }
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const handleImageError = (imageSrc) => {
    setImageLoadErrors(prev => new Set([...prev, imageSrc]));
  };

  const getImageSrc = (src) => {
    return imageLoadErrors.has(src) ? `https://via.placeholder.com/400x300/2563eb/ffffff?text=RACOHI+Image` : src;
  };

const missionVision = [
  {
    icon: Eye,
    title: "Our Vision",
    content: "To have a Rakai Community with a controlled epidemic of HIV, TB and Malaria with empowered and healthy Girls & Women.",
    color: "blue",
    stats: "Integrated Health Approach"
  },
  {
    icon: Target,
    title: "Our Mission", 
    content: "To have a community with reduced new HIV, TB and Malaria Infections who are responsive to treatment and prevention strategies and have empowered and healthy women through comprehensive Prevention, response and empowerment strategies.",
    color: "teal",
    stats: "Dual-Focus Strategy"
  }
];

  const coreValues = [
    { icon: Heart, title: "Integrity", description: "Transparent and ethical practices in all our operations" },
    { icon: Users, title: "Empowerment", description: "Building capacity and confidence in communities" },
    { icon: Shield, title: "Community-Centered", description: "Putting community needs at the heart of our work" },
    { icon: TrendingUp, title: "Sustainability", description: "Creating lasting change for future generations" },
    { icon: Award, title: "Gender Equality", description: "Promoting equal rights and opportunities for all" },
    { icon: Star, title: "Evidence-Based", description: "Using research and data to guide our interventions" }
  ];

  const impactStats = [
    { number: '2,000+', label: 'Girls & Women Targeted', icon: Users },
    { number: '130+', label: 'Healthcare Providers to be Trained', icon: Award },  
    { number: '18', label: 'Months Implementation', icon: Target },
    { number: '85%', label: 'Positive Outcomes', icon: TrendingUp }
  ];

  const keyFacts = [
    { stat: '41.4%', label: 'Women experienced verbal abuse', trend: 'baseline' },
    { stat: '31.3%', label: 'Experienced physical violence', trend: 'baseline' },
    { stat: '30.0%', label: 'Experienced sexual violence', trend: 'baseline' },
    { stat: '11%', label: 'Adolescent girls experienced coercion', trend: 'baseline' }
  ];

  const organizationDetails = [
    { icon: MapPin, title: 'Physical Address', info: ['Buyamba Trading Centre', 'Rakai District, Uganda'] },
    { icon: Mail, title: 'Postal Address', info: ['P.O. Box 277', 'Kyotera – Uganda'] },
    { icon: Phone, title: 'Phone Numbers', info: ['+256 701 816 912', '+256 778 173 081'] },
    { icon: Globe, title: 'Email Address', info: ['rakaicommunityhealthinitiative@gmail.com'] }
  ];

  return (
    <div className="about-page">
      {/* Bootstrap CSS */}
      <link 
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" 
        rel="stylesheet" 
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background"></div>
        <div className="hero-overlay"></div>
        <div className="container position-relative">
          <div className="row align-items-center min-vh-80">
            <div className="col-lg-8">
              <div className="hero-content">
                <div className="hero-badge">About RACOHI</div>
                <div className="hero-badge">About RACOHI</div>
<h1 className="hero-title">
  Transforming Health & Empowering Communities
</h1>
<p className="hero-description">
  Rakai Community Health Initiative tackles the dual challenge of infectious diseases 
  (HIV, TB, Malaria) and gender-based violence through integrated, community-led 
  interventions that create sustainable health and social outcomes.
</p>
                <div className="hero-stats">
                  {impactStats.slice(0, 2).map((stat, index) => (
                    <div key={index} className="stat-highlight">
                      <stat.icon size={20} className="stat-icon" />
                      <div className="stat-content">
                        <div className="stat-number">{stat.number}</div>
                        <div className="stat-label">{stat.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="about-visual">
                <div className="images-container">
                  <div className="image-wrapper primary-image">
                    <img 
                      src={getImageSrc("images/cover.jpg")}
                      alt="RACOHI team working with community members"
                      className="hero-image"
                      onError={() => handleImageError("images/cover.jpg")}
                      loading="lazy"
                    />
                    <div className="image-overlay">
                      <div className="overlay-content">
                        <span className="overlay-text">RACOHI</span>
                      </div>
                    </div>
                  </div>
                  <div className="image-wrapper secondary-image">
                    <img 
                      src={getImageSrc("images/cover2.jpg")}
                      alt="Community empowerment program"
                      className="hero-image"
                      onError={() => handleImageError("images/cover2.jpg")}
                      loading="lazy"
                    />
                    <div className="image-badge">
                      <Heart size={16} className="badge-icon" />
                      <span>Impact</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section 
        className="mission-vision-section"
        ref={el => sectionRefs.current.mission = el}
        data-section="mission"
      >
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Our Foundation</div>
            <h2 className="section-title">Guided by Purpose</h2>
            <p className="section-subtitle">
              Our vision and mission drive everything we do, shaping our approach 
              to creating lasting change in communities.
            </p>
          </div>

          <div className="row g-4">
            {missionVision.map((item, index) => (
              <div key={index} className="col-lg-6">
                <div 
                  className={`mission-card ${visibleSections.has('mission') ? 'animate-in' : ''}`}
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <div className="card-header">
                    <div className={`card-icon ${item.color}`}>
                      <item.icon size={28} />
                    </div>
                    <div className="card-meta">
                      <h3 className="card-title">{item.title}</h3>
                      <div className="card-stats">{item.stats}</div>
                    </div>
                  </div>
                  <div className="card-content">
                    <p className="card-text">{item.content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section 
        className="story-section"
        ref={el => sectionRefs.current.story = el}
        data-section="story"
      >
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <div className={`story-content ${visibleSections.has('story') ? 'animate-in' : ''}`}>
                <div className="section-badge">Our Story</div>
                <h2 className="story-title">Understanding the Challenge</h2>
                <div className="story-text">
                  <p>
                    Rakai District, located in Uganda's Central Region, has been at the forefront 
                    of public health challenges, notably HIV/AIDS. Despite concerted efforts, 
                    Sexual and Gender-Based Violence (SGBV) remains a pervasive issue.
                  </p>
                  <p>
                    Our research reveals that nearly half of women in Rakai have experienced 
                    some form of violence, highlighting the urgent need for comprehensive 
                    intervention strategies that address root causes and provide sustainable solutions.
                  </p>
                </div>
                
                <div className="key-insights">
                  <h4 className="insights-title">Key Research Findings</h4>
                  <div className="insights-grid">
                    {keyFacts.map((fact, index) => (
                      <div key={index} className="insight-item">
                        <div className="insight-stat">{fact.stat}</div>
                        <div className="insight-label">{fact.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className={`story-visual ${visibleSections.has('story') ? 'animate-in' : ''}`}>
                <div className="images-showcase">
                  <div className="main-image-container">
                    <img 
                      src={getImageSrc("images/cover2.jpg")}
                      alt="Community Health Work" 
                      className="story-image main"
                      onError={() => handleImageError("images/cover2.jpg")}
                      loading="lazy"
                    />
                    <div className="image-overlay-story"></div>
                  </div>
                  
                  <div className="community-image-container">
                    <img 
                      src={getImageSrc("images/community/comm1.jpg")}
                      alt="Community empowerment in action" 
                      className="story-image community"
                      onError={() => handleImageError("images/community/comm1.jpg")}
                      loading="lazy"
                    />
                  </div>
                </div>
                
                <div className="visual-stats">
                  {impactStats.slice(2).map((stat, index) => (
                    <div key={index} className="floating-stat">
                      <stat.icon size={16} />
                      <span>{stat.number}</span>
                      <small>{stat.label}</small>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section 
        className="values-section"
        ref={el => sectionRefs.current.values = el}
        data-section="values"
      >
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Our Values</div>
            <h2 className="section-title">What Drives Us</h2>
            <p className="section-subtitle">
              Our core values shape our culture, guide our decisions, and 
              define how we work with communities and partners.
            </p>
          </div>

          <div className="values-grid">
            {coreValues.map((value, index) => (
              <div 
                key={index} 
                className={`value-card ${visibleSections.has('values') ? 'animate-in' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="value-icon">
                  <value.icon size={24} />
                </div>
                <h4 className="value-title">{value.title}</h4>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Organization Details */}
      <section 
        className="details-section"
        ref={el => sectionRefs.current.details = el}
        data-section="details"
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className={`details-card ${visibleSections.has('details') ? 'animate-in' : ''}`}>
                <div className="details-header">
                  <div className="section-badge">Contact Information</div>
                  <h2 className="details-title">Get in Touch</h2>
                  <p className="details-subtitle">
                    Multiple ways to reach us for partnerships, support, or collaboration opportunities.
                  </p>
                </div>
                
                <div className="contact-grid">
                  {organizationDetails.map((detail, index) => (
                    <div key={index} className="contact-item">
                      <div className="contact-icon">
                        <detail.icon size={20} />
                      </div>
                      <div className="contact-content">
                        <h5 className="contact-title">{detail.title}</h5>
                        <div className="contact-info">
                          {detail.info.map((line, idx) => (
                            <div key={idx} className="info-line">{line}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Styles */}
      <style jsx>{`
        :root {
          --primary-blue: #2563eb;
          --secondary-blue: #1e40af;
          --accent-teal: #06b6d4;
          --accent-orange: #f59e0b;
          --text-dark: #1f2937;
          --text-gray: #6b7280;
          --text-light: #9ca3af;
          --white: #ffffff;
          --light-bg: #f8fafc;
          --border-light: rgba(0, 0, 0, 0.06);
          --success-green: #10b981;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          line-height: 1.6;
          color: var(--text-dark);
          padding-top: 80px;
        }

        /* Hero Section */
        .hero-section {
          position: relative;
          min-height: 80vh;
          display: flex;
          align-items: center;
          overflow: hidden;
        }

        .hero-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, var(--light-bg) 0%, #e0f2fe 100%);
        }

        .hero-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="rgba(37,99,235,0.05)"/><circle cx="80" cy="80" r="1.5" fill="rgba(6,182,212,0.03)"/></svg>') repeat;
          background-size: 100px 100px;
        }

        .min-vh-80 {
          min-height: 80vh;
        }

        .hero-content {
          padding: 2rem 0rem;
          position: relative;
          z-index: 2;
          max-width: 600px;
        }

        .hero-badge {
          display: inline-block;
          padding: 8px 16px;
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary-blue);
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 24px;
        }

        .hero-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 24px;
          line-height: 1.1;
        }

        .hero-description {
          font-size: 1.2rem;
          color: var(--text-gray);
          margin-bottom: 32px;
          line-height: 1.6;
        }

        .hero-stats {
          display: flex;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .stat-highlight {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        }

        .stat-icon {
          color: var(--primary-blue);
        }

        .stat-number {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--primary-blue);
          line-height: 1;
        }

        .stat-label {
          font-size: 0.9rem;
          color: var(--text-gray);
          font-weight: 500;
        }

        /* About Visual Section */
        .about-visual {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: 400px;
        }

        .images-container {
          position: relative;
          width: 350px;
          height: 400px;
        }

        .image-wrapper {
          position: absolute;
          border-radius: 1.5rem;
          overflow: hidden;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
        }

        .primary-image {
          top: 0;
          left: 0;
          width: 280px;
          height: 350px;
          z-index: 2;
          animation: primaryFloat 6s ease-in-out infinite;
        }

        .secondary-image {
          bottom: 0;
          right: 0;
          width: 200px;
          height: 250px;
          z-index: 1;
          animation: secondaryFloat 6s ease-in-out infinite;
          animation-delay: 3s;
        }

        .hero-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          transition: transform 0.3s ease;
          background: linear-gradient(45deg, #f0f0f0, #e0e0e0);
        }

        .image-wrapper:hover .hero-image {
          transform: scale(1.05);
        }

        .image-wrapper:hover {
          transform: translateY(-5px);
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.25);
        }

        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(to top, rgba(37, 99, 235, 0.9), transparent);
          padding: 1.5rem;
          color: white;
        }

        .overlay-content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .overlay-text {
          font-size: 1.2rem;
          font-weight: 700;
          letter-spacing: 1px;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }

        .image-badge {
          position: absolute;
          top: 1rem;
          left: 1rem;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          padding: 0.5rem 1rem;
          border-radius: 50px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--primary-blue);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .badge-icon {
          color: var(--accent-teal);
        }

        .images-container::before {
          content: '';
          position: absolute;
          top: 20px;
          right: 20px;
          width: 60px;
          height: 60px;
          background: linear-gradient(45deg, var(--accent-orange), var(--accent-teal));
          border-radius: 50%;
          opacity: 0.1;
          animation: decorFloat 8s ease-in-out infinite;
        }

        .images-container::after {
          content: '';
          position: absolute;
          bottom: 30px;
          left: 30px;
          width: 40px;
          height: 40px;
          background: linear-gradient(45deg, var(--success-green), var(--primary-blue));
          border-radius: 30%;
          opacity: 0.08;
          animation: decorFloat 6s ease-in-out infinite reverse;
        }

        @keyframes primaryFloat {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(1deg);
          }
        }

        @keyframes secondaryFloat {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(-1deg);
          }
        }

        @keyframes decorFloat {
          0%, 100% {
            transform: translateY(0px) scale(1);
          }
          50% {
            transform: translateY(-20px) scale(1.1);
          }
        }

        /* Section Styles */
        .mission-vision-section,
        .story-section,
        .values-section,
        .details-section {
          padding: 20px 0;
        }

        .story-section {
          background: white;
        }

        .values-section {
          background: var(--light-bg);
        }

        .details-section {
          background: white;
        }

        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-badge {
          display: inline-block;
          padding: 8px 16px;
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary-blue);
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .section-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 16px;
        }

        .section-subtitle {
          font-size: 1.1rem;
          color: var(--text-gray);
          max-width: 600px;
          margin: 0 auto;
        }

        /* Mission Vision Cards */
        .mission-card {
          background: white;
          border-radius: 16px;
          padding: 32px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
          height: 100%;
          transition: all 0.3s ease;
          position: relative;
        }

        .mission-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--primary-blue), var(--accent-teal));
          border-radius: 16px 16px 0 0;
        }

        .mission-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
        }

        .card-header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 20px;
        }

        .card-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .card-icon.blue {
          background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
        }

        .card-icon.teal {
          background: linear-gradient(135deg, var(--accent-teal), #0891b2);
        }

        .card-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 4px;
        }

        .card-stats {
          font-size: 0.9rem;
          color: var(--text-light);
          font-weight: 500;
        }

        .card-content {
          padding-left: 76px;
        }

        .card-text {
          font-size: 1rem;
          color: var(--text-gray);
          line-height: 1.6;
        }

        /* Story Section */
        .story-content {
          padding-right: 2rem;
        }

        .story-title {
          font-size: clamp(2rem, 3vw, 2.5rem);
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 24px;
        }

        .story-text p {
          font-size: 1.1rem;
          color: var(--text-gray);
          margin-bottom: 20px;
          line-height: 1.6;
        }

        .key-insights {
          margin-top: 40px;
          padding: 24px;
          background: var(--light-bg);
          border-radius: 12px;
        }

        .insights-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 20px;
        }

        .insights-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .insight-item {
          text-align: center;
          padding: 16px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .insight-stat {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--primary-blue);
          margin-bottom: 4px;
        }

        .insight-label {
          font-size: 0.85rem;
          color: var(--text-gray);
          line-height: 1.3;
        }

        .story-visual {
          position: relative;
          padding-left: 2rem;
        }

        .images-showcase {
          position: relative;
          height: 450px;
        }

        .main-image-container {
          position: relative;
          width: 100%;
          height: 320px;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
          z-index: 2;
        }

        .community-image-container {
          position: absolute;
          bottom: 0;
          right: -20px;
          width: 300px;
          height: 200px;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
          border: 4px solid white;
          z-index: 3;
          animation: communityFloat 6s ease-in-out infinite;
        }

        .story-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          transition: transform 0.3s ease;
          background: linear-gradient(45deg, #f0f0f0, #e0e0e0);
        }

        .story-image.main {
          height: 320px;
        }

        .story-image.community {
          height: 180px;
        }

        .community-image-container:hover .story-image.community {
          transform: scale(1.05);
        }

        .image-overlay-story {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.1), rgba(6, 182, 212, 0.1));
        }
        @keyframes communityFloat {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-8px) rotate(1deg);
          }
        }

        .visual-stats {
          position: absolute;
          top: 20px;
          right: 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .floating-stat {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-radius: 8px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--primary-blue);
        }

        .floating-stat svg {
          color: var(--accent-teal);
        }

        .floating-stat small {
          font-size: 0.75rem;
          color: var(--text-gray);
          font-weight: 500;
        }

        /* Values Grid */
        .values-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
        }

        .value-card {
          background: white;
          padding: 32px 24px;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
          position: relative;
        }

        .value-card::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 40px;
          height: 3px;
          background: linear-gradient(90deg, var(--primary-blue), var(--accent-teal));
          border-radius: 2px;
        }

        .value-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        .value-icon {
          width: 60px;
          height: 60px;
          margin: 0 auto 20px;
          background: linear-gradient(135deg, var(--primary-blue), var(--accent-teal));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .value-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 12px;
        }

        .value-description {
          color: var(--text-gray);
          line-height: 1.5;
        }

        /* Details Section */
        .details-card {
          background: white;
          border-radius: 20px;
          padding: 48px;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.08);
        }

        .details-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .details-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 16px;
        }

        .details-subtitle {
          font-size: 1.1rem;
          color: var(--text-gray);
          max-width: 500px;
          margin: 0 auto;
        }

        .contact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 32px;
          margin-bottom: 48px;
        }

        .contact-item {
          display: flex;
          gap: 16px;
        }

        .contact-icon {
          width: 48px;
          height: 48px;
          background: rgba(37, 99, 235, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary-blue);
          flex-shrink: 0;
        }

        .contact-content {
          flex: 1;
        }

        .contact-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 8px;
        }

        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .info-line {
          color: var(--text-gray);
          font-size: 0.95rem;
          line-height: 1.4;
        }

        /* CTA Section */
        .cta-section {
          text-align: center;
          padding: 40px;
          background: var(--light-bg);
          border-radius: 16px;
        }

        .cta-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 12px;
        }

        .cta-text {
          color: var(--text-gray);
          margin-bottom: 24px;
        }

        .cta-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn-primary,
        .btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
        }

        .btn-primary {
          background: var(--accent-orange);
          color: white;
        }

        .btn-primary:hover {
          background: #d97706;
          transform: translateY(-2px);
        }

        .btn-secondary {
          background: white;
          color: var(--primary-blue);
          border: 2px solid var(--primary-blue);
        }

        .btn-secondary:hover {
          background: var(--primary-blue);
          color: white;
        }

        /* Animations */
        .animate-in {
          animation: slideInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive Design */
        @media (max-width: 992px) {
          .about-visual {
            margin-top: 3rem;
            min-height: 300px;
          }

          .images-container {
            width: 300px;
            height: 350px;
          }

          .primary-image {
            width: 240px;
            height: 300px;
          }

          .secondary-image {
            width: 180px;
            height: 220px;
          }

          .story-content {
            padding-right: 0;
            margin-bottom: 2rem;
          }

          .story-visual {
            padding-left: 0;
          }
        }

        @media (max-width: 768px) {
          body {
            padding-top: 60px;
          }

          .about-visual {
            margin-top: 2rem;
            min-height: 250px;
          }

          .images-container {
            width: 250px;
            height: 300px;
          }

          .primary-image {
            width: 200px;
            height: 250px;
          }

          .secondary-image {
            width: 150px;
            height: 180px;
          }

          .overlay-text {
            font-size: 1rem;
          }

          .image-badge {
            font-size: 0.7rem;
            padding: 0.4rem 0.8rem;
          }

          .mission-card {
            padding: 24px 20px;
          }

          .card-header {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }

          .card-content {
            padding-left: 0;
            text-align: center;
          }

          .value-card {
            padding: 24px 20px;
          }

          .contact-item {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }

          .details-card {
            padding: 32px 24px;
          }

          .community-image-container {
            right: -10px;
            width: 300px;
            height: 140px;
          }

          .main-image-container {
            height: 280px;
          }

          .images-showcase {
            height: 400px;
          }

          .hero-stats {
            justify-content: center;
          }

          .stat-highlight {
            flex-direction: column;
            text-align: center;
            gap: 8px;
            padding: 12px 16px;
          }
        }

        @media (max-width: 576px) {
          .about-visual {
            display: none;
          }

          .hero-content {
            text-align: center;
          }

          .visual-stats {
            position: static;
            flex-direction: row;
            justify-content: center;
            margin-top: 16px;
          }

          .floating-stat {
            flex-direction: column;
            text-align: center;
            gap: 4px;
            padding: 8px 12px;
          }

          .insights-grid {
            grid-template-columns: 1fr;
          }

          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .btn-primary,
          .btn-secondary {
            width: 100%;
            max-width: 250px;
            justify-content: center;
          }

          .hero-title {
            font-size: 2rem;
          }

          .section-title {
            font-size: 1.8rem;
          }

          .details-title {
            font-size: 2rem;
          }
        }

        /* Accessibility */
        @media (prefers-reduced-motion: reduce) {
          .primary-image,
          .secondary-image,
          .images-container::before,
          .images-container::after,
          .community-image-container {
            animation: none;
          }
          
          .animate-in {
            animation: none;
          }
          
          .mission-card:hover,
          .value-card:hover,
          .image-wrapper:hover {
            transform: none;
          }
        }

        /* Focus States */
        .btn-primary:focus,
        .btn-secondary:focus {
          outline: 2px solid var(--accent-orange);
          outline-offset: 2px;
        }

        /* Print Styles */
        @media print {
          .hero-section {
            min-height: auto;
            padding: 2rem 0;
          }
          
          .cta-section {
            display: none;
          }

          .about-visual,
          .story-visual {
            display: none;
          }
        }

        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: var(--light-bg);
        }

        ::-webkit-scrollbar-thumb {
          background: var(--primary-blue);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: var(--secondary-blue);
        }

        /* Enhanced hover effects */
        .mission-card:hover::before {
          height: 6px;
        }

        .value-card:hover::after {
          width: 60px;
          height: 4px;
        }

        /* Image Loading States */
        .hero-image,
        .story-image {
          min-height: 200px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hero-image[src*="placeholder"],
        .story-image[src*="placeholder"] {
          font-size: 14px;
          font-weight: 600;
          color: white;
          text-align: center;
          background: var(--primary-blue) !important;
        }

        /* Enhanced Visual Depth */
        .mission-card,
        .value-card,
        .details-card {
          position: relative;
          overflow: hidden;
        }

        .mission-card::before,
        .value-card::after {
          transition: all 0.3s ease;
        }

        /* Smooth transitions */
        html {
          scroll-behavior: smooth;
        }

        /* High Contrast Mode */
        @media (prefers-contrast: high) {
          .hero-background {
            background: var(--white);
          }
          
          .section-badge {
            background: var(--primary-blue);
            color: var(--white);
          }

          .floating-stat,
          .image-badge {
            background: var(--white);
            border: 2px solid var(--primary-blue);
          }
        }

        /* Image Error Handling */
        .hero-image,
        .story-image {
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }

        /* Loading Animation */
        .hero-image:not([src*="placeholder"]),
        .story-image:not([src*="placeholder"]) {
          opacity: 0;
          animation: fadeIn 0.5s ease-in-out forwards;
        }

        @keyframes fadeIn {
          to {
            opacity: 1;
          }
        }

        /* Enhanced Typography */
        .hero-title,
        .section-title,
        .story-title,
        .details-title {
          letter-spacing: -0.02em;
        }

        /* Better Button Interactions */
        .btn-primary:active,
        .btn-secondary:active {
          transform: translateY(0);
        }
      `}</style>

      {/* Bootstrap JS */}
      <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/js/bootstrap.bundle.min.js"></script>
    </div>
  );
};

export default AboutPage;